import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import {
  FlexCells,
  ReportTable,
  ReportTd,
  ReportThead,
  ReportTr,
  SubsetCells,
  TextCell,
} from '@/features/mold/components';
import type { ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel } from '@/features/mold/models';
import { PAPER_FILE_SIZE } from '@/features/mold/utils';
import { getSumOfTheEndOfThePriorFundBalance } from '@/features/project/review-sheet/utils/ks-local-government/execution-progress';
import { FALLBACK_TEXT, formatAmountForPdf, formatNumberForPdf } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  fiscalYear?: number;
  localGovernmentFundIndividualReport?: z.infer<
    typeof ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel
  >;
};

export const TableFundOwnerForLocalGovernment: FC<Props> = ({
  fiscalYear,
  localGovernmentFundIndividualReport,
}) => {
  const { t } = useTranslation();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  const previousFiscalYear = fiscalYear && fiscalYear - 1;

  const { bodyData, bodyNegativeCount } = useMemo(() => {
    const _data =
      localGovernmentFundIndividualReport?.data.map((v) => {
        const { totalAmount, negativeCount } =
          getSumOfTheEndOfThePriorFundBalance({
            fundBusinessForLocalGovernment: v,
          });
        return {
          ...v,
          totalAmount,
          totalAmountNegativeCount: negativeCount ?? 0,
        };
      }) ?? [];

    return {
      bodyData: _data,
      bodyNegativeCount: _data.reduce(
        (acc, curr) => acc + curr.totalAmountNegativeCount,
        0,
      ),
    };
  }, [localGovernmentFundIndividualReport?.data]);
  const summary = localGovernmentFundIndividualReport?.summaryData;

  const cellNumberInARow = 24;
  const calcWidthStyle = (width: number) =>
    `calc((${width} / 10) * (${PAPER_FILE_SIZE.A4.height} / ${cellNumberInARow}))`;

  const getCountAndAmountText = (
    count: number | undefined | null,
    amount: number | undefined | null,
    negativeCount?: number | null,
    isDebugMode?: boolean,
  ) => {
    return `(${formatNumberForPdf(count)})\n${formatAmountForPdf(amount, {
      isDebugMode,
      negativeCount,
    })}`;
  };

  const AMOUNT_COLUMN_WIDTH = 11;

  return (
    <ReportTable>
      <ReportThead>
        <ReportTr>
          <ReportTd widthStyle={calcWidthStyle(3)}>
            <TextCell
              align="center"
              text={t('report-fund-owner-for-local-government-index')}
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(8)}>
            <TextCell
              align="center"
              text={t('fund-owner-for-local-government')}
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(10)}>
            <TextCell align="center" text={t('fund-name')} />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(15)}>
            <TextCell
              align="center"
              text={t('fund-overview-for-local-government')}
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH * 2)}>
            <SubsetCells
              containedCell={
                <TextCell
                  align="center"
                  text={t(
                    'report-local-government-fund-header-beginning-of-the-fund-balance',
                    { year: previousFiscalYear && previousFiscalYear - 1 },
                  )}
                />
              }
              includedCell={
                <TextCell
                  align="center"
                  text={t(
                    'report-standard-fund-private-header-national-equivalent-of-the-balance',
                  )}
                />
              }
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH * 7)}>
            <FlexCells vertical cellRatios={['1em', 1]}>
              <TextCell
                align="center"
                text={t(
                  'report-local-government-fund-header-income-and-expenditure',
                  { year: previousFiscalYear },
                )}
              />

              <FlexCells cellRatios={[6, 1]}>
                <SubsetCells
                  cellColRatios={[1, 5]}
                  cellRowRatios={['1em', 1]}
                  containedCell={
                    <TextCell
                      text={t('report-local-government-fund-header-income')}
                    />
                  }
                  includedCell={
                    <SubsetCells
                      cellColRatios={[1, 4]}
                      cellRowRatios={['1em', 1]}
                      containedCell={
                        <TextCell
                          text={t(
                            'report-standard-fund-private-header-national-equivalent-of-the-balance',
                          )}
                        />
                      }
                      includedCell={
                        <FlexCells cellRatios={[3, 1]}>
                          <FlexCells vertical cellRatios={['1em', 1]}>
                            <TextCell
                              align="center"
                              text={t('granted-amount-by-the-country')}
                            />

                            <FlexCells>
                              <TextCell align="center" text={t('initial')} />
                              <TextCell align="center" text={t('correction')} />
                              <TextCell
                                align="center"
                                text={t('reserve-budget')}
                              />
                            </FlexCells>
                          </FlexCells>

                          <TextCell align="center" text={t('others')} />
                        </FlexCells>
                      }
                    />
                  }
                />

                <TextCell
                  align="center"
                  text={t('report-local-government-fund-header-expense', {
                    year: previousFiscalYear,
                  })}
                />
              </FlexCells>
            </FlexCells>
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH)}>
            <TextCell
              align="center"
              text={t(
                'report-local-government-fund-header-amount-of-national-treasury-return',
                { year: previousFiscalYear },
              )}
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH * 2)}>
            <SubsetCells
              containedCell={
                <TextCell
                  align="center"
                  text={t(
                    'report-local-government-fund-header-ending-of-the-fund-balance',
                    { year: previousFiscalYear },
                  )}
                />
              }
              includedCell={
                <TextCell
                  align="center"
                  text={t(
                    'report-standard-fund-private-header-national-equivalent-of-the-balance',
                  )}
                />
              }
            />
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH * 5)}>
            <FlexCells vertical cellRatios={['1em', 1]}>
              <TextCell
                align="center"
                text={t(
                  'report-local-government-fund-header-decision-on-project-implementation',
                  { year: previousFiscalYear },
                )}
              />
              <FlexCells vertical borderStyle="dotted" cellRatios={[3, 2]}>
                <FlexCells>
                  <TextCell
                    align="center"
                    text={`${t('fund-business-subsidy')}\n${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}`}
                  />
                  <TextCell align="center" text={t('investment')} />
                  <TextCell align="center" text={t('loan')} />
                  <TextCell align="center" text={t('debt-guarantee')} />
                  <TextCell
                    align="center"
                    text={t('fund-business-for-local-government-others')}
                  />
                </FlexCells>
                <FlexCells>
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                </FlexCells>
              </FlexCells>
            </FlexCells>
          </ReportTd>
          <ReportTd widthStyle={calcWidthStyle(AMOUNT_COLUMN_WIDTH * 3)}>
            <FlexCells vertical cellRatios={['1em', 1]}>
              <TextCell
                align="center"
                text={t('report-local-government-fund-header-loan-balance', {
                  year: previousFiscalYear,
                })}
              />
              <FlexCells vertical borderStyle="dotted" cellRatios={[3, 2]}>
                <FlexCells>
                  <TextCell align="center" text={t('investment')} />
                  <TextCell align="center" text={t('loan')} />
                  <TextCell align="center" text={t('debt-guarantee')} />
                </FlexCells>
                <FlexCells>
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                  <TextCell
                    align="center"
                    text={t('count-and-amount-with-br')}
                  />
                </FlexCells>
              </FlexCells>
            </FlexCells>
          </ReportTd>
        </ReportTr>
      </ReportThead>

      <tbody>
        {bodyData.map((item, index) => {
          return (
            <ReportTr key={`${item.localFundOwner}-${index}`}>
              <ReportTd>
                <TextCell align="center" text={`${item.number}`} />
              </ReportTd>
              <ReportTd>
                <TextCell align="left" text={item.localFundOwner} />
              </ReportTd>
              <ReportTd>
                <TextCell align="left" text={item.localFundName} />
              </ReportTd>
              <ReportTd>
                <TextCell align="left" text={item.localFundOverview} />
              </ReportTd>
              <ReportTd>
                <FlexCells>
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(item.beginningOfTheFundBalance)}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(
                      item.nationalEquivalentOfBeginningOfTheFundBalance,
                    )}
                  />
                </FlexCells>
              </ReportTd>
              <ReportTd>
                <FlexCells cellRatios={[6, 1]}>
                  <FlexCells cellRatios={[1, 5]}>
                    <TextCell
                      align="right"
                      paddingHorizontal="small"
                      text={formatAmountForPdf(item.incomeAmount)}
                    />

                    <FlexCells cellRatios={[1, 4]}>
                      <TextCell
                        align="right"
                        paddingHorizontal="small"
                        text={formatAmountForPdf(
                          item.nationalEquivalentOfIncomeAmount,
                        )}
                      />

                      <FlexCells cellRatios={[3, 1]}>
                        <FlexCells>
                          <TextCell
                            align="right"
                            paddingHorizontal="small"
                            text={formatAmountForPdf(
                              item.grantedAmountByTheCountryFromInitial,
                            )}
                          />
                          <TextCell
                            align="right"
                            paddingHorizontal="small"
                            text={formatAmountForPdf(
                              item.grantedAmountByTheCountryFromSupplementary,
                            )}
                          />
                          <TextCell
                            align="right"
                            paddingHorizontal="small"
                            text={formatAmountForPdf(
                              item.grantedAmountByTheCountryFromReserve,
                            )}
                          />
                        </FlexCells>

                        <TextCell
                          align="right"
                          paddingHorizontal="small"
                          text={formatAmountForPdf(item.otherIncomeAmount)}
                        />
                      </FlexCells>
                    </FlexCells>
                  </FlexCells>

                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(item.businessExpense)}
                  />
                </FlexCells>
              </ReportTd>
              <ReportTd>
                <TextCell
                  align="right"
                  paddingHorizontal="small"
                  text={formatAmountForPdf(
                    item.amountReturnedToTheNationalTreasury,
                  )}
                />
              </ReportTd>
              <ReportTd>
                <FlexCells>
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(item.totalAmount, {
                      isDebugMode,
                      negativeCount: item.totalAmountNegativeCount,
                    })}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(
                      item.nationalEquivalentOfTheEndOfTheFundBalance,
                    )}
                  />
                </FlexCells>
              </ReportTd>
              <ReportTd>
                <FlexCells>
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.decidedNumberOfSubsidy,
                      item.decidedAmountOfSubsidy,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.decidedNumberOfInvestment,
                      item.decidedAmountOfInvestment,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.decidedNumberOfLoan,
                      item.decidedAmountOfLoan,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.decidedNumberOfDebtGuarantee,
                      item.decidedAmountOfDebtGuarantee,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.decidedNumberOfOthers,
                      item.decidedAmountOfOthers,
                    )}
                  />
                </FlexCells>
              </ReportTd>
              <ReportTd>
                <FlexCells>
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.balanceNumberOfInvestment,
                      item.balanceAmountOfInvestment,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.balanceNumberOfLoan,
                      item.balanceAmountOfLoan,
                    )}
                  />
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={getCountAndAmountText(
                      item.balanceNumberOfDebtGuarantee,
                      item.balanceAmountOfDebtGuarantee,
                    )}
                  />
                </FlexCells>
              </ReportTd>
            </ReportTr>
          );
        })}

        <ReportTr>
          <ReportTd>
            <TextCell
              align="center"
              text={t(
                'report-fund-owner-for-local-government-fund-owner-total-amount',
              )}
            />
          </ReportTd>
          <ReportTd>
            <TextCell
              align="center"
              text={summary?.numberOfLocalFundOwner || FALLBACK_TEXT}
            />
          </ReportTd>
          <ReportTd>
            <TextCell text="" />
          </ReportTd>
          <ReportTd>
            <TextCell text="" />
          </ReportTd>
          <ReportTd>
            <FlexCells>
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={formatAmountForPdf(summary?.beginningOfTheFundBalance, {
                  isDebugMode,
                  negativeCount:
                    summary?.negativeBeginningOfTheFundBalanceCount,
                })}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={formatAmountForPdf(
                  summary?.nationalEquivalentOfBeginningOfTheFundBalance,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeNationalEquivalentOfBeginningOfTheFundBalanceCount,
                  },
                )}
              />
            </FlexCells>
          </ReportTd>
          <ReportTd>
            <FlexCells cellRatios={[6, 1]}>
              <FlexCells cellRatios={[1, 5]}>
                <TextCell
                  align="right"
                  paddingHorizontal="small"
                  text={formatAmountForPdf(summary?.incomeAmount, {
                    isDebugMode,
                    negativeCount: summary?.negativeIncomeAmountCount,
                  })}
                />

                <FlexCells cellRatios={[1, 4]}>
                  <TextCell
                    align="right"
                    paddingHorizontal="small"
                    text={formatAmountForPdf(
                      summary?.nationalEquivalentOfIncomeAmount,
                      {
                        isDebugMode,
                        negativeCount:
                          summary?.negativeNationalEquivalentOfIncomeAmountCount,
                      },
                    )}
                  />

                  <FlexCells cellRatios={[3, 1]}>
                    <FlexCells>
                      <TextCell
                        align="right"
                        paddingHorizontal="small"
                        text={formatAmountForPdf(
                          summary?.grantedAmountByTheCountryFromInitial,
                          {
                            isDebugMode,
                            negativeCount:
                              summary?.negativeGrantedAmountByTheCountryFromInitialCount,
                          },
                        )}
                      />
                      <TextCell
                        align="right"
                        paddingHorizontal="small"
                        text={formatAmountForPdf(
                          summary?.grantedAmountByTheCountryFromSupplementary,
                          {
                            isDebugMode,
                            negativeCount:
                              summary?.negativeGrantedAmountByTheCountryFromSupplementaryCount,
                          },
                        )}
                      />
                      <TextCell
                        align="right"
                        paddingHorizontal="small"
                        text={formatAmountForPdf(
                          summary?.grantedAmountByTheCountryFromReserve,
                          {
                            isDebugMode,
                            negativeCount:
                              summary?.negativeGrantedAmountByTheCountryFromReserveCount,
                          },
                        )}
                      />
                    </FlexCells>

                    <TextCell
                      align="right"
                      paddingHorizontal="small"
                      text={formatAmountForPdf(summary?.otherIncomeAmount, {
                        isDebugMode,
                        negativeCount: summary?.negativeOtherIncomeAmountCount,
                      })}
                    />
                  </FlexCells>
                </FlexCells>
              </FlexCells>

              <TextCell
                align="right"
                paddingHorizontal="small"
                text={formatAmountForPdf(summary?.businessExpense, {
                  isDebugMode,
                  negativeCount: summary?.negativeBusinessExpenseCount,
                })}
              />
            </FlexCells>
          </ReportTd>
          <ReportTd>
            <TextCell
              align="right"
              paddingHorizontal="small"
              text={formatAmountForPdf(
                summary?.amountReturnedToTheNationalTreasury,
                {
                  isDebugMode,
                  negativeCount:
                    summary?.negativeAmountReturnedToTheNationalTreasuryCount,
                },
              )}
            />
          </ReportTd>
          <ReportTd>
            <FlexCells>
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={formatAmountForPdf(summary?.endingOfTheFundBalance, {
                  isDebugMode,
                  negativeCount: bodyNegativeCount,
                })}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={formatAmountForPdf(
                  summary?.nationalEquivalentOfTheEndOfTheFundBalance,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeNationalEquivalentOfTheEndOfTheFundBalanceCount,
                  },
                )}
              />
            </FlexCells>
          </ReportTd>
          <ReportTd>
            <FlexCells>
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.decidedNumberOfSubsidy,
                  summary?.decidedAmountOfSubsidy,
                  summary?.negativeDecidedAmountOfSubsidyCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.decidedNumberOfInvestment,
                  summary?.decidedAmountOfInvestment,
                  summary?.negativeDecidedAmountOfInvestmentCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.decidedNumberOfLoan,
                  summary?.decidedAmountOfLoan,
                  summary?.negativeDecidedAmountOfLoanCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.decidedNumberOfDebtGuarantee,
                  summary?.decidedAmountOfDebtGuarantee,
                  summary?.negativeDecidedAmountOfDebtGuaranteeCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.decidedNumberOfOthers,
                  summary?.decidedAmountOfOthers,
                  summary?.negativeDecidedAmountOfOthersCount,
                  isDebugMode,
                )}
              />
            </FlexCells>
          </ReportTd>
          <ReportTd>
            <FlexCells>
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.balanceNumberOfInvestment,
                  summary?.balanceAmountOfInvestment,
                  summary?.negativeBalanceAmountOfInvestmentCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.balanceNumberOfLoan,
                  summary?.balanceAmountOfLoan,
                  summary?.negativeBalanceAmountOfLoanCount,
                  isDebugMode,
                )}
              />
              <TextCell
                align="right"
                paddingHorizontal="small"
                text={getCountAndAmountText(
                  summary?.balanceNumberOfDebtGuarantee,
                  summary?.balanceAmountOfDebtGuarantee,
                  summary?.negativeBalanceAmountOfDebtGuaranteeCount,
                  isDebugMode,
                )}
              />
            </FlexCells>
          </ReportTd>
        </ReportTr>
      </tbody>
    </ReportTable>
  );
};
